import React, { useState } from "react";
import {
  FieldType,
  FieldTypeType,
  OptionType,
  SubOptionType,
} from "../../../services/helpers/types";
import { Divider } from "@mui/material";
import DraggableSelectOptionsList from "../DraggableSelectOptionsList/DraggableSelectOptionsList";
import Button from "../Button/Button";
import { Add } from "@mui/icons-material";
import CascaderField from "../../organisms/CascaderField/CascaderField";
import {
  getNewOptionID,
  getNewSubOptionID,
} from "../../../services/helpers/commonHelpers";
import { useTranslation } from "react-i18next";

interface AddCascaderFieldProps {
  field?: FieldType;
  onFieldChange: (name: string, value?: any, defaultValue?: string) => void;
}

const AddCascaderField = (props: AddCascaderFieldProps) => {
  const { field, onFieldChange } = props;

  const { t } = useTranslation();

  const [defaultValue, setDefaultValue] = useState<string | undefined>(
    field?.defaultValue ?? undefined,
  );

  const [newOptions, setNewOptions] = useState<OptionType[]>(
    field?.options ?? [],
  );

  const [customField, setCustomField] = useState<FieldType | undefined>(field);

  const handleDefaultValueChange = (value?: string) => {
    setDefaultValue(value);
    onFieldChange("defaultValue", value);
  };

  const setDefaultValueSelectOptions = (
    options: OptionType[],
    defaultValue?: string,
  ) => {
    if (customField) {
      setCustomField({ ...customField, options: options });
    } else {
      setCustomField({
        fieldID: 0,
        name: "Možnosti",
        position: 0,
        type: FieldTypeType.Cascader,
        noteOption: false,
        defaultValue: "",
        options: options,
        tabID: 0,
        placeholder: "",
        value: "",
      });
    }
    if (defaultValue !== undefined) {
      setDefaultValue(defaultValue);
    }
    onFieldChange("options", options, defaultValue);
  };

  const handleAddField = () => {
    const newOptionID = getNewOptionID(newOptions, t);
    const addNewOptions: OptionType[] = [
      ...newOptions,
      {
        name: `${t("newOption")} ${newOptionID}`,
        value: `${t("newOption")} ${newOptionID}`,
        position: newOptions.length + 1,
        optionID: newOptionID,
        fieldID: 0,
      },
    ];
    setDefaultValueSelectOptions(addNewOptions);
    setNewOptions(addNewOptions);
  };

  const handleDelete = (itemID: number) => {
    let newDefaultValue = undefined;

    const deletedOption = newOptions.find(
      (option) => option.position === itemID,
    );

    if (defaultValue !== undefined && deletedOption !== undefined) {
      const defaultValueArray = defaultValue.split("; ");
      const newDefaultValueArray = defaultValueArray
        .map((value) => {
          const splitValue = value.split(" - ");
          const baseOptionValue = splitValue[0];
          if (baseOptionValue === deletedOption.value) {
            return undefined;
          }
          return value;
        })
        .filter((value) => value !== undefined);
      newDefaultValue = newDefaultValueArray.join("; ");
    }

    const deleteNewOptions = newOptions
      .filter((option) => option.position !== itemID)
      .map((value, index) => {
        return { ...value, position: index + 1 };
      });

    setDefaultValueSelectOptions(deleteNewOptions, newDefaultValue);
    setNewOptions(deleteNewOptions);
  };

  const handleOptionChange = (option: OptionType) => {
    const position = option.position;
    const oldOption = newOptions.find((value) => value.position === position);
    let newDefaultValue = undefined;
    // if option is already selected and value is changed, change default value too
    if (oldOption) {
      if (oldOption.value !== option.value && defaultValue) {
        newDefaultValue = defaultValue.replace(oldOption.value, option.value);
      }
    }
    const changeNewOptions = newOptions.map((value) => {
      if (value.position === option.position) {
        return option;
      }
      return value;
    });
    // when suboption is deleted remove it from default value
    if (
      option.subOptions?.length !== oldOption?.subOptions?.length &&
      defaultValue
    ) {
      const newSubOptions = option.subOptions ?? [];
      const oldSubOptions = oldOption?.subOptions;
      if (oldSubOptions !== undefined) {
        const deletedSubOption = oldSubOptions?.find(
          (item) => !newSubOptions.some((p) => p.value === item.value),
        );
        if (deletedSubOption !== undefined) {
          const newDefaultValueBaseArray = defaultValue.split("; ");
          const newDefaultValueArray = newDefaultValueBaseArray.map((value) => {
            const splitValue = value.split(" - ");
            const baseOptionValue = splitValue[0];
            const baseOptionSubOptionsValue = splitValue[1];
            if (baseOptionValue === option.value && baseOptionSubOptionsValue) {
              const defaultValueSubOptionsArray =
                baseOptionSubOptionsValue.split("|");
              const newDefaultValueSubOptionsArray =
                defaultValueSubOptionsArray.filter(
                  (subOptionValue) => subOptionValue !== deletedSubOption.value,
                );
              if (newDefaultValueSubOptionsArray.length === 0) {
                return baseOptionValue;
              }
              return `${baseOptionValue} - ${newDefaultValueSubOptionsArray.join("|")}`;
            }
            return value;
          });
          newDefaultValue = newDefaultValueArray.join("; ");
        }
      }
    }

    setDefaultValueSelectOptions(changeNewOptions, newDefaultValue);
    setNewOptions(changeNewOptions);
  };

  const handlePositionChange = (newItemsPositions: number[]) => {
    const reorderedNewOptions: OptionType[] = newItemsPositions.map(
      (position, index) => {
        const newOption = newOptions.find(
          (option) => option.position === position,
        );
        if (!newOption) {
          return newOptions[index];
        }
        return { ...newOption, position: index + 1 };
      },
    );
    setNewOptions(reorderedNewOptions);
    onFieldChange("options", reorderedNewOptions);
  };

  const handleCreateSubOption = (option?: OptionType) => {
    const newOptionIndex = newOptions.findIndex(
      (item) => item.optionID === option?.optionID,
    );

    const newOptionID = getNewSubOptionID(option?.subOptions ?? [], t);

    const newOption: SubOptionType = {
      name: `${t("newSubOption")} ${newOptionID}`,
      value: `${t("newSubOption")} ${newOptionID}`,
      position: (option?.subOptions?.length ?? 0) + 1,
      subOptionID: newOptionID,
      optionID: option?.optionID ?? 0,
    };

    if (newOptionIndex !== -1) {
      const newOptionArray = [...newOptions];
      newOptionArray[newOptionIndex] = {
        ...newOptionArray[newOptionIndex],
        subOptions: [
          ...(newOptionArray[newOptionIndex].subOptions ?? []),
          newOption,
        ],
      };
      setNewOptions(newOptionArray);

      setDefaultValueSelectOptions(newOptionArray);
    }
  };

  const handleSubOptionsChange = (subOption: SubOptionType) => {
    const newOptionIndex = newOptions.findIndex(
      (item) => item.optionID === subOption.optionID,
    );

    const currentBaseOption = newOptions[newOptionIndex];

    const oldSubOption = currentBaseOption?.subOptions?.find(
      (item) => item.subOptionID === subOption.subOptionID,
    );

    let newDefaultValue = undefined;

    if (defaultValue !== undefined && oldSubOption !== undefined) {
      const defaultValueArray = defaultValue.split("; ");

      const newDefaultValueArray = defaultValueArray.map((value) => {
        const splitValue = value.split(" - ");
        if (splitValue.length > 1) {
          const currentBaseOptionInDefaultValue = splitValue[0];
          if (currentBaseOptionInDefaultValue === currentBaseOption.value) {
            const currentChangedSubOptionsValue = splitValue[1];
            const newSubOptionsValueArray =
              currentChangedSubOptionsValue.split("|");
            const newDefaultSubOptionValue = newSubOptionsValueArray.map(
              (subOptionValue) => {
                if (subOptionValue === oldSubOption.value) {
                  return subOption.value;
                }
                return subOptionValue;
              },
            );
            const newDefaultSubOptionValueString =
              newDefaultSubOptionValue.join("|");

            return `${currentBaseOptionInDefaultValue} - ${newDefaultSubOptionValueString}`;
          }
        }
        return value;
      });
      newDefaultValue = newDefaultValueArray.join("; ");
    }

    const newSubOptionIndex = currentBaseOption?.subOptions?.findIndex(
      (item) => item.subOptionID === subOption.subOptionID,
    );

    const newSubOptionArray = [...(currentBaseOption.subOptions ?? [])];
    newSubOptionArray[newSubOptionIndex ?? 0] = {
      ...newSubOptionArray[newSubOptionIndex ?? 0],
      ...subOption,
    };

    const newOptionArray = [...newOptions];
    newOptionArray[newOptionIndex] = {
      ...newOptionArray[newOptionIndex],
      subOptions: newSubOptionArray,
    };

    setNewOptions(newOptionArray);

    setDefaultValueSelectOptions(newOptionArray, newDefaultValue);
  };
  return (
    <div className="dui-add-cascader-field">
      <Divider className="dui-divider" />
      <div className="dui-add-cascader-field-values">
        <div className="dui-add-cascader-field-values-title">
          {t("selectOptions")}:
        </div>
        <DraggableSelectOptionsList
          type={FieldTypeType.Cascader}
          options={newOptions}
          onDeleteItem={handleDelete}
          onOptionChange={handleOptionChange}
          onChangePosition={handlePositionChange}
          onCreateSubOption={handleCreateSubOption}
          onSubOptionsChange={handleSubOptionsChange}
        />
        <div
          className="dui-fields-edit-add-button-wrapper"
          onClick={handleAddField}
        >
          <Button
            icon={<Add />}
            type="primary"
            corners="circle"
            className="dui-fields-edit-add-button"
          />
        </div>
      </div>
      <Divider className="dui-divider" />
      <div className="dui-example-field">
        <div className="dui-example-field-title">{t("defaultValue")}:</div>
        <CascaderField
          field={customField}
          value={defaultValue}
          onChange={handleDefaultValueChange}
        />
      </div>
    </div>
  );
};

export default AddCascaderField;
